import { hapticFeedback } from '@tma.js/sdk';
import {
    type KeyboardEvent as ReactKeyboardEvent,
    type PointerEvent as ReactPointerEvent,
    type RefObject,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FLOATING_TABBAR_BAR_HEIGHT } from '../../helpers/floatingTabbarLayout';
import { TABBAR_INDICATOR_EDGE_MARGIN, TABBAR_INDICATOR_INSET_X, TABBAR_INDICATOR_INSET_Y } from './constants';
import type { FloatingTab } from './types';

type UseFloatingTabStripArgs = {
    tabs: FloatingTab[];
    selectedId: string;
    onSelect: (id: string) => void;
};

type IndicatorLayout = {
    x: number;
    width: number;
    height: number;
};

type UseFloatingTabStripResult = {
    stripRef: RefObject<HTMLDivElement>;
    isPressed: boolean;
    displayIndex: number;
    focusedIndex: number;
    indicatorLayout: IndicatorLayout | null;
    indicatorMotionReady: boolean;
    onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => void;
    onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
};

export function useFloatingTabStrip({ tabs, selectedId, onSelect }: UseFloatingTabStripArgs): UseFloatingTabStripResult {
    const stripRef = useRef<HTMLDivElement>(null);
    const previewIndexRef = useRef(0);
    const [isPressed, setIsPressed] = useState(false);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [indicatorLayout, setIndicatorLayout] = useState<IndicatorLayout | null>(null);
    const [indicatorMotionReady, setIndicatorMotionReady] = useState(false);

    const selectedIndex = useMemo(() => {
        const index = tabs.findIndex(tab => tab.id === selectedId);
        return index >= 0 ? index : 0;
    }, [selectedId, tabs]);

    useEffect(() => {
        previewIndexRef.current = previewIndex;
    }, [previewIndex]);

    useEffect(() => {
        if (!isPressed) setPreviewIndex(selectedIndex);
    }, [selectedIndex, isPressed]);

    useEffect(() => {
        setFocusedIndex(selectedIndex);
    }, [selectedIndex]);

    const displayIndex = isPressed ? previewIndex : selectedIndex;

    const measureIndicator = useCallback(() => {
        const strip = stripRef.current;
        if (!strip) return;

        const tabNodes = strip.querySelectorAll<HTMLElement>('[role="tab"]');
        const tab = tabNodes[displayIndex];
        if (!tab) {
            setIndicatorLayout(null);
            return;
        }

        const rawX = tab.offsetLeft + TABBAR_INDICATOR_INSET_X;
        const rawWidth = Math.max(0, tab.offsetWidth - TABBAR_INDICATOR_INSET_X * 2);
        const x = Math.max(TABBAR_INDICATOR_EDGE_MARGIN, rawX);
        const width = Math.min(rawX + rawWidth, strip.clientWidth - TABBAR_INDICATOR_EDGE_MARGIN) - x;

        setIndicatorLayout({
            x,
            width: Math.max(0, width),
            height: FLOATING_TABBAR_BAR_HEIGHT - TABBAR_INDICATOR_INSET_Y * 2,
        });
    }, [displayIndex]);

    useLayoutEffect(() => {
        measureIndicator();
        const strip = stripRef.current;
        if (!strip) return undefined;

        const observer = new ResizeObserver(measureIndicator);
        observer.observe(strip);
        return () => observer.disconnect();
    }, [measureIndicator, tabs.length]);

    useLayoutEffect(() => {
        if (!indicatorLayout || indicatorMotionReady) return undefined;
        const id = requestAnimationFrame(() => setIndicatorMotionReady(true));
        return () => cancelAnimationFrame(id);
    }, [indicatorLayout, indicatorMotionReady]);

    const applyPreviewIndex = useCallback((nextIndex: number) => {
        if (previewIndexRef.current !== nextIndex) hapticFeedback.selectionChanged.ifAvailable();
        previewIndexRef.current = nextIndex;
        setPreviewIndex(nextIndex);
    }, []);

    const selectTabAtIndex = useCallback(
        (index: number) => {
            const tab = tabs[index];
            if (!tab) return;
            setFocusedIndex(index);
            if (tab.id !== selectedId) onSelect(tab.id);
        },
        [onSelect, selectedId, tabs],
    );

    const indexFromClientX = useCallback(
        (clientX: number) => {
            const strip = stripRef.current;
            if (!strip || tabs.length === 0) return selectedIndex;

            const tabNodes = strip.querySelectorAll<HTMLElement>('[role="tab"]');
            if (tabNodes.length === 0) return selectedIndex;

            for (let index = 0; index < tabNodes.length; index += 1) {
                const { left, right } = tabNodes[index].getBoundingClientRect();
                const isLast = index === tabNodes.length - 1;
                if (clientX >= left && (isLast ? clientX <= right : clientX < right)) return index;
            }

            return selectedIndex;
        },
        [selectedIndex, tabs.length],
    );

    const setPreviewFromClientX = useCallback(
        (clientX: number) => {
            applyPreviewIndex(indexFromClientX(clientX));
        },
        [applyPreviewIndex, indexFromClientX],
    );

    const finishInteraction = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            const { pointerId, currentTarget, clientX } = event;
            if (currentTarget.hasPointerCapture(pointerId)) currentTarget.releasePointerCapture(pointerId);
            const index = indexFromClientX(clientX);
            applyPreviewIndex(index);
            setIsPressed(false);
            selectTabAtIndex(index);
        },
        [applyPreviewIndex, indexFromClientX, selectTabAtIndex],
    );

    const onPointerDown = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            event.preventDefault();
            event.currentTarget.setPointerCapture(event.pointerId);
            setIsPressed(true);
            setPreviewFromClientX(event.clientX);
        },
        [setPreviewFromClientX],
    );

    const onPointerMove = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
            setPreviewFromClientX(event.clientX);
        },
        [setPreviewFromClientX],
    );

    const onPointerUp = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            finishInteraction(event);
        },
        [finishInteraction],
    );

    const onPointerCancel = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>) => {
            const { pointerId, currentTarget } = event;
            if (currentTarget.hasPointerCapture(pointerId)) currentTarget.releasePointerCapture(pointerId);
            setIsPressed(false);
            applyPreviewIndex(selectedIndex);
        },
        [applyPreviewIndex, selectedIndex],
    );

    const onKeyDown = useCallback(
        (event: ReactKeyboardEvent<HTMLDivElement>) => {
            if (tabs.length === 0) return;

            let nextIndex: number | null = null;
            switch (event.key) {
                case 'ArrowRight':
                    nextIndex = Math.min(tabs.length - 1, focusedIndex + 1);
                    break;
                case 'ArrowLeft':
                    nextIndex = Math.max(0, focusedIndex - 1);
                    break;
                case 'Home':
                    nextIndex = 0;
                    break;
                case 'End':
                    nextIndex = tabs.length - 1;
                    break;
                default:
                    return;
            }

            event.preventDefault();
            selectTabAtIndex(nextIndex);
        },
        [focusedIndex, selectTabAtIndex, tabs.length],
    );

    return {
        stripRef,
        isPressed,
        displayIndex,
        focusedIndex,
        indicatorLayout,
        indicatorMotionReady,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        onKeyDown,
    };
}
